/**
 * Stored images on CloudFront (epic 2026-10-02-cloudfront-media). An image's
 * URL is `<base_url>/<its S3 key>`: the distribution strips `/media` and reads
 * that key from the avatars bucket, so no request to the API is needed.
 */

export type StoredImageKind = "avatars" | "logos";

/** What the web needs from `GET /media/session`. `null` = CloudFront media is
 *  off, or not known yet: presign as before. */
export interface MediaSession {
  baseUrl: string;
  logos: boolean;
  avatars: boolean;
  expiresAt: number;
}

/** `<base>/<key>`, each key segment percent-encoded, `?v=` when given. */
export function mediaUrl(baseUrl: string, s3Key: string, version?: string | number | null): string {
  const path = s3Key.split("/").map(encodeURIComponent).join("/");
  const url = `${baseUrl.replace(/\/+$/, "")}/${path}`;
  return version === undefined || version === null || version === ""
    ? url
    : `${url}?v=${encodeURIComponent(String(version))}`;
}

/** Whether `kind` loads from CloudFront in this session. */
export function servesKind(session: MediaSession | null, kind: StoredImageKind): boolean {
  if (!session) return false;
  return kind === "avatars" ? session.avatars : session.logos;
}

/** Key → CloudFront URL, for every key (no network). `versions` adds `?v=` to
 *  the keys it names — a key whose object is REWRITTEN in place (the Thrive
 *  photo at `avatars/thrive/<id>.jpg`, re-fetched after its row expires) needs
 *  it so the browser's cache of the old bytes is not what renders. */
export function mediaUrlMap(
  baseUrl: string,
  s3Keys: readonly string[],
  versions?: ReadonlyMap<string, string | number>
): Map<string, string> {
  return new Map(s3Keys.map((key) => [key, mediaUrl(baseUrl, key, versions?.get(key))]));
}
